/**
 * Studio sidebar — two stacked StickyFolder sections (Kernels, Canvases)
 * with their item listings rendered directly in the sidebar drawer.
 *
 * Replaces the flat AdminMenu when the rail's Studio mode is active.
 * Selecting an item updates `?view=<kernels|canvases>&kernel|canvas=<id>`
 * so the main canvas swaps to the matching detail body.
 *
 * Visual primitives are reused from ScopeSidebar/SidebarNavRuntime
 * (DimHeader for section headers, FolderListRow for item rows) so the
 * row chrome — active green-bar, hover, typography — is guaranteed
 * identical to the scope explorer's sidebar.
 */
import { useMemo, useState } from "react";
import StickyFolder, { FolderList } from "./StickyFolder.jsx";
import ScrollArea from "./ScrollArea.jsx";
import TabStrip from "./TabStrip.jsx";
import { DimHeader, NavOverviewRow } from "./ScopeSidebar.jsx";
import { FolderListRow } from "./SidebarNavRuntime.jsx";
import KernelAuthorBadge from "./KernelAuthorBadge.jsx";
import { cellCount } from "../lib/canvasLayout.js";
import { listKernelCatalogCandidates } from "../lib/kernelStore.js";

// One section = StickyFolder + DimHeader + a list of FolderListRows.
function Section({ label, open, onToggle, items, emptyKind, renderRow }) {
    return (
        <StickyFolder
            header={
                <DimHeader
                    label={label}
                    count={items.length}
                    expanded={open}
                    onClick={onToggle}
                />
            }
            open={open}
            padded
        >
            {items.length === 0 ? (
                <div className="px-[12px] py-[10px] text-[11px] leading-[1.4] text-[color:var(--muted-soft)]">
                    No saved {emptyKind} yet.
                </div>
            ) : (
                items.map(renderRow)
            )}
        </StickyFolder>
    );
}

export default function StudioMenu({
    kernels,
    canvases,
    workflows = [],
    selectedView,           // "kernels" | "canvases" | "workflows" | "skills" | null
    selectedItemId,         // command (kernels/workflows) or slug (canvases) or null
    onSelectItem,           // (view, itemId) => void; pass (null, null) for overview
    onCollapseDrawer,       // optional — collapses the sidebar entirely
    skillsVisible = false,
}) {
    // Both sections default to open so the user sees the full inventory
    // immediately on landing. Manual toggle is per-session only — the
    // lists are short enough that persistence isn't worth the state.
    const [openKernels, setOpenKernels] = useState(true);
    const [openCanvases, setOpenCanvases] = useState(true);
    const [openWorkflows, setOpenWorkflows] = useState(true);

    const headerTabs = [{ id: "studio", label: "Library", active: true }];

    // Header action — drawer collapse only. The rail mode buttons
    // already handle "leave this mode," so no separate close-menu
    // affordance is needed here.
    const headerActions = onCollapseDrawer ? (
        <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
            <button
                type="button"
                onClick={onCollapseDrawer}
                title="Collapse drawer"
                aria-label="Collapse drawer"
                className="inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors [&_svg]:block [&_svg]:h-[12px] [&_svg]:w-[12px]"
            >
                <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 3.5 5.5 8 10 12.5" />
                </svg>
            </button>
        </div>
    ) : null;

    const sortedKernelCandidates = useMemo(
        () => listKernelCatalogCandidates(null),
        [kernels],
    );
    const sortedCanvases = useMemo(
        () => [...canvases].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
        [canvases],
    );
    const sortedWorkflows = useMemo(
        () => [...workflows].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
        [workflows],
    );

    // Overview is active when nothing's selected — but NOT when the
    // itemless Workflows view is up (it owns its own active state).
    const overviewActive = !selectedView;

    return (
        <nav
            data-admin-menu
            className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]"
            aria-label="Studio navigation"
        >
            <TabStrip
                tabs={headerTabs}
                actions={headerActions}
                className="pt-[11px] max-[900px]:pt-[8px]"
            />
            {/* OverlayScrollbars-wrapped viewport. data-sidebar-scroll is
                required by both the global stylesheet (which sets
                overflow:hidden on the host so OS's inner viewport owns
                scrolling) and by StickyFolder's findScrollRoot helper. */}
            <ScrollArea
                data-sidebar-scroll
                className="min-h-0 flex-1"
                contentClassName="flex min-h-full flex-col pt-[6px] pb-[12px]"
            >
                <FolderList>
                    {/* Overview row — shared NavOverviewRow so its
                        uppercase + letter-spaced label and active
                        green-bar match ScopeSidebar exactly. */}
                    <NavOverviewRow
                        active={overviewActive}
                        onClick={() => onSelectItem(null, null)}
                    />
                    <Section
                        label="Kernels"
                        open={openKernels}
                        onToggle={() => setOpenKernels((v) => !v)}
                        items={sortedKernelCandidates}
                        emptyKind="kernels"
                        renderRow={(candidate) => {
                            const k = candidate.resolvedDefinition.body;
                            const shadowed = !candidate.isWinner;
                            return (
                                <FolderListRow
                                    key={`${candidate.id || k.id || candidate.command}:${candidate.tier}`}
                                    as={shadowed ? "div" : "button"}
                                    title={`/${k.command}`}
                                    note={
                                        <span className="grid min-w-0 gap-[2px]">
                                            <span>
                                                {k.title}
                                                {shadowed
                                                    ? " · shadowed"
                                                    : k.builtin
                                                        ? " · built-in"
                                                        : ""}
                                            </span>
                                            <KernelAuthorBadge candidate={candidate} />
                                        </span>
                                    }
                                    active={
                                        candidate.isWinner
                                        && selectedView === "kernels"
                                        && k.command === selectedItemId
                                    }
                                    onClick={shadowed
                                        ? undefined
                                        : () => onSelectItem("kernels", k.command)}
                                />
                            );
                        }}
                    />
                    <Section
                        label="Canvases"
                        open={openCanvases}
                        onToggle={() => setOpenCanvases((v) => !v)}
                        items={sortedCanvases}
                        emptyKind="canvases"
                        renderRow={(c) => (
                            <FolderListRow
                                key={c.id || c.slug}
                                as="button"
                                title={`/${c.slug}`}
                                note={c.builtin ? `${c.title} · built-in` : c.title}
                                meta={cellCount(c)}
                                active={selectedView === "canvases" && c.slug === selectedItemId}
                                onClick={() => onSelectItem("canvases", c.slug)}
                            />
                        )}
                    />
                    {/* Workflows — saved agentic analyses (config + run
                        history). Kept at the BOTTOM: still experimental.
                        Creating one goes through the Library "+" menu, same
                        as kernels/canvases — no inline create row here. */}
                    <Section
                        label="Workflows"
                        open={openWorkflows}
                        onToggle={() => setOpenWorkflows((v) => !v)}
                        items={sortedWorkflows}
                        emptyKind="workflows"
                        renderRow={(w) => (
                            <FolderListRow
                                key={w.id || w.command}
                                as="button"
                                title={`/${w.command}`}
                                note={w.title}
                                active={selectedView === "workflows" && w.command === selectedItemId}
                                onClick={() => onSelectItem("workflows", w.command)}
                            />
                        )}
                    />
                    {skillsVisible ? (
                        <FolderListRow
                            as="button"
                            title="Skills"
                            note="Managed and observed AI Agent skills"
                            active={selectedView === "skills"}
                            onClick={() => onSelectItem("skills", null)}
                        />
                    ) : null}
                </FolderList>
            </ScrollArea>
        </nav>
    );
}
